import { useEffect, useMemo, useState } from 'react';

import { initializeRadarIfNeeded, setRadarUserId } from '@sg/radar-fe';

import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import { useCustomerData, useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useBraze } from '../../context/Braze';
import { useSnapPixel } from '../../integrations';
import { useRadarForegroundTracking } from '../useRadarForegroundTracking';
import { useSyncRadarMarketingPushTags } from '../useSyncRadarMarketingPushTags';

// ─── Constants ───────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Initializes third-party vendors integrations and synchronizes current customer data
 */
export const use3rdPartyIntegrations = () => {
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const [isRadarReady, setIsRadarReady] = useState(false);

  const customerData = useCustomerData();
  const { id, trackingUuid, firstName, lastName, email, phoneNumber } = customerData ?? {}; // prettier-ignore

  // NOTE: We extract required customer data for 3rd party vendors to prevent
  //       extra effects
  const customer = useMemo(
    () => ({ id, trackingUuid, firstName, lastName, email, phoneNumber }),
    [id, trackingUuid, firstName, lastName, email, phoneNumber],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  // NOTE: WE extract helpers individually to prevent unnecessary effects
  //       because of internal state updates

  const {
    initialize: initTelemetry,
    identify: syncTelemetryCustomer,
    reset: resetTelemetryCustomer,
  } = useTelemetry();

  const { init: initBraze, destroy: destroyBraze, syncCustomer: syncBrazeCustomer } = useBraze();

  // ─── Telemetry ───────────────────────────────────────────────────────

  useEffect(() => {
    void initTelemetry();
  }, [initTelemetry]);

  useEffect(() => {
    if (!customer.id) return;

    syncTelemetryCustomer(customer);

    return () => {
      resetTelemetryCustomer();
    };
  }, [customer, resetTelemetryCustomer, syncTelemetryCustomer]);

  // ─── Snap Pixel ──────────────────────────────────────────────────────

  const { init: initSnapPixel } = useSnapPixel();

  useEffect(() => {
    if (isLoggingIn) return;

    initSnapPixel(customer.id ? { firstName, lastName, email, phoneNumber } : undefined); // prettier-ignore
  }, [customer, email, firstName, initSnapPixel, isLoggingIn, lastName, phoneNumber]);

  // ─── Braze ───────────────────────────────────────────────────────────

  useEffect(() => {
    initBraze?.();

    return () => {
      destroyBraze?.();
    };
  }, [destroyBraze, initBraze]);

  useEffect(() => {
    if (!customer.id) return;

    syncBrazeCustomer(customer);
  }, [customer, syncBrazeCustomer]);

  // ─── Radar ────────────────────────────────────────────────────────────

  useEffect(() => {
    void initializeRadarIfNeeded(RADAR_SDK_KEY).then(() => {
      setIsRadarReady(true);
    });
  }, []);

  useEffect(() => {
    if (!isRadarReady || !customer.trackingUuid) return;

    setRadarUserId(customer.trackingUuid);
  }, [customer.trackingUuid, isRadarReady]);

  useRadarForegroundTracking({
    isCustomerLoggedIn: isLoggedIn,
    isReady: isRadarReady && !isLoggingIn,
  });
  useSyncRadarMarketingPushTags({ isReady: isRadarReady });
};
